/**
 * Prioris — Materials Approval family-fields admin modal.
 *
 * Admin-only. Lets the logged-in admin define custom spec-attribute fields
 * on any family (built-in or custom):
 *   - e.g. aggregates → LA value (number), Fines % (number), Fraction (dropdown),
 *          Petrographic OK (boolean), Chloride (number, ppm)
 *   - e.g. rebar → Grade (dropdown), Re (number), Rm (number), Diameter (number)
 *
 * Fields support types: text, number, date, dropdown, boolean.
 * "Show in grid" flag surfaces the field as a column in the submittal grid.
 *
 * Persisted via PUT /api/prioris/ma/families/{key}/custom-fields.
 */
import { useEffect, useState } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import { X, Plus, Trash2, Loader2, AlertCircle, Settings, Check } from 'lucide-react';

const TYPE_LABELS = {
  text: 'Text', number: 'Number', date: 'Date', dropdown: 'Dropdown', boolean: 'Yes/No',
};

export default function MaFamilyFieldsModal({ t, onClose, onSaved, initialFamilyKey = null }) {
  const [families, setFamilies] = useState([]);
  const [selectedKey, setSelectedKey] = useState(initialFamilyKey);
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchSln('/api/prioris/ma/families').then(d => {
      setFamilies(d.families || []);
      const pickKey = initialFamilyKey || (d.families?.[0]?.key);
      setSelectedKey(pickKey);
    }).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, [initialFamilyKey]);

  useEffect(() => {
    if (!selectedKey) return;
    const f = families.find(x => x.key === selectedKey);
    setFields((f?.custom_fields || []).map(cf => ({ ...cf, options: (cf.options || []).join(', ') })));
  }, [selectedKey, families]);

  const addField = () => {
    setFields(prev => [...prev, { name: '', label: '', type: 'text', required: false, options: '', show_in_grid: false, unit: '' }]);
  };
  const updateField = (i, patch) => setFields(prev => prev.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const removeField = (i) => setFields(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true); setErr('');
    const payload = {
      fields: fields.map(f => ({
        name: (f.name || '').trim(),
        label: (f.label || '').trim(),
        type: f.type || 'text',
        required: !!f.required,
        options: f.type === 'dropdown'
          ? (f.options || '').split(',').map(s => s.trim()).filter(Boolean)
          : [],
        show_in_grid: !!f.show_in_grid,
        unit: (f.unit || '').trim(),
      })),
    };
    try {
      const res = await fetchSln(`/api/prioris/ma/families/${selectedKey}/custom-fields`, {
        method: 'PUT', body: JSON.stringify(payload),
      });
      setFamilies(res.family || []);
      onSaved && onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const current = families.find(f => f.key === selectedKey);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} data-testid="ma-fields-modal">
      <div className="rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: t.cardBorder }}>
          <Settings className="w-4 h-4" style={{ color: t.accent }} />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold">Manage custom fields</h3>
            <p className="text-[11px]" style={{ color: t.textDim }}>
              Add measured properties that matter for each <strong>material type</strong> (e.g. concrete mix, rebar, aggregate). These are independent of the spec section — the same <em>concrete mix</em> custom field applies whether the RAM sits under HKCEDD-16.2 or FP24-500. They show up in the submittal drawer and can optionally be surfaced as grid columns.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="ma-fields-close">
            <X className="w-4 h-4" style={{ color: t.textMuted }} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: t.accent }} /></div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <label className="text-[11px] font-semibold" style={{ color: t.textDim }}>Material type</label>
                <select value={selectedKey || ''} onChange={e => setSelectedKey(e.target.value)}
                  className="rounded px-2 py-1 text-[12px]"
                  style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
                  data-testid="ma-fields-family">
                  {families.map(f => (
                    <option key={f.key} value={f.key}>
                      {f.label}{f.builtin ? '' : ' (custom)'} · {(f.custom_fields || []).length} fields
                    </option>
                  ))}
                </select>
                {current && <span className="text-[11px]" style={{ color: t.textDim }}>Code: <code style={{ color: t.text }}>{current.code}</code></span>}
              </div>

              {/* Fields table */}
              <div className="overflow-x-auto border rounded" style={{ borderColor: t.cardBorder }}>
                <table className="text-[11px] w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr style={{ background: t.inputBg }}>
                      {['Name (key)', 'Label', 'Type', 'Unit', 'Options (dropdown only)', 'Required', 'Show in grid', ''].map(h => (
                        <th key={h} className="px-2 py-1.5 text-left font-semibold" style={{ color: t.textMuted, borderBottom: `1px solid ${t.cardBorder}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f, i) => (
                      <tr key={i} data-testid={`ma-fields-row-${i}`}>
                        <td className="px-1 py-1" style={cellStyle(t)}>
                          <input value={f.name} onChange={e => updateField(i, { name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                            placeholder="e.g. la_value"
                            className="w-full rounded px-1.5 py-0.5 font-mono text-[11px]"
                            style={inputStyle(t)} />
                        </td>
                        <td className="px-1 py-1" style={cellStyle(t)}>
                          <input value={f.label} onChange={e => updateField(i, { label: e.target.value })}
                            placeholder="e.g. LA value"
                            className="w-full rounded px-1.5 py-0.5 text-[11px]"
                            style={inputStyle(t)} />
                        </td>
                        <td className="px-1 py-1" style={cellStyle(t)}>
                          <select value={f.type} onChange={e => updateField(i, { type: e.target.value })}
                            className="rounded px-1 py-0.5 text-[11px]" style={inputStyle(t)}>
                            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </td>
                        <td className="px-1 py-1" style={cellStyle(t)}>
                          <input value={f.unit || ''} onChange={e => updateField(i, { unit: e.target.value })}
                            placeholder="%, ppm, MPa…"
                            className="w-16 rounded px-1.5 py-0.5 text-[11px]"
                            style={inputStyle(t)} />
                        </td>
                        <td className="px-1 py-1" style={cellStyle(t)}>
                          <input value={f.options} onChange={e => updateField(i, { options: e.target.value })}
                            placeholder={f.type === 'dropdown' ? 'comma-separated' : '—'}
                            disabled={f.type !== 'dropdown'}
                            className="w-full rounded px-1.5 py-0.5 text-[11px] disabled:opacity-40"
                            style={inputStyle(t)} />
                        </td>
                        <td className="px-1 py-1 text-center" style={cellStyle(t)}>
                          <input type="checkbox" checked={!!f.required} onChange={e => updateField(i, { required: e.target.checked })} />
                        </td>
                        <td className="px-1 py-1 text-center" style={cellStyle(t)}>
                          <input type="checkbox" checked={!!f.show_in_grid} onChange={e => updateField(i, { show_in_grid: e.target.checked })} />
                        </td>
                        <td className="px-1 py-1 text-center" style={cellStyle(t)}>
                          <button onClick={() => removeField(i)}
                            className="p-1 rounded hover:bg-red-500/10"
                            data-testid={`ma-fields-remove-${i}`}>
                            <Trash2 className="w-2.5 h-2.5" style={{ color: '#f87171' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={8} className="px-2 py-1.5 cursor-pointer"
                        style={{ background: t.inputBg, color: t.accent }}
                        onClick={addField} data-testid="ma-fields-add">
                        <span className="inline-flex items-center gap-1 text-[11px]"><Plus className="w-3 h-3" /> Add field</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {fields.length === 0 && (
                <p className="text-[11px] mt-3" style={{ color: t.textDim }}>
                  No custom fields yet for {current?.label || 'this material type'}. Add fields like "LA value" (number), "Fraction" (dropdown), etc.
                </p>
              )}
            </>
          )}

          {err && (
            <div className="mt-3 px-2 py-1 text-[11px] rounded flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}>
              <AlertCircle className="w-3 h-3" /> {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex justify-end gap-2" style={{ borderColor: t.cardBorder }}>
          <button onClick={onClose} className="px-3 py-1 rounded text-[11px]" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
          <button onClick={save} disabled={saving || !selectedKey}
            className="px-3 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1"
            style={{ background: t.accent, color: '#000' }}
            data-testid="ma-fields-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Save fields
          </button>
        </div>
      </div>
    </div>
  );
}

const cellStyle = (t) => ({ borderBottom: `1px solid ${t.cardBorder}` });
const inputStyle = (t) => ({ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text });
